/* Netlify Image CDN routing for Firebase Storage images.

   cdnImage() rewrites a team's Firebase Storage URL to `/.netlify/images?url=…&w=…`, so Netlify
   resizes it and serves AVIF/WebP (negotiated from the browser's Accept header, so no `fm`).
   Only the bucket allowlisted in netlify.toml `[images] remote_images` is routed; everything
   else (local imports, YouTube/Instagram, demo URLs, empty values) passes through unchanged.

   Routing is on only when the build flag `imageCdn` is true (generateTeamConfig writes
   `process.env.NETLIFY === 'true'`, so Firebase-only tenants and local builds never route) and
   the CDN has not been disabled for this browser session by the fallback below. */
import { imageCdn } from './TeamInfo';

export const FIREBASE_STORAGE_PREFIX = 'https://firebasestorage.googleapis.com/v0/b/optimx-sports.appspot.com/';
const CDN_PATH = '/.netlify/images';
const CDN_MARKER = `${CDN_PATH}?`;
const SESSION_KEY = 'optimx-image-cdn-disabled';
const FAILURES_BEFORE_DISABLE = 3;

/* Requested widths (px), ~2x the largest CSS size the image is shown at. */
export const ImageWidth = {
    ICON: 128,
    LOGO: 256,
    THUMB: 480,
    CARD: 800,
    BANNER: 1600,
    HERO: [800, 1200, 1600, 2000],
};

let sessionDisabled: boolean | null = null; // null = not read from sessionStorage yet
let failureCount = 0;
let fallbackInstalled = false;

const isSessionDisabled = (): boolean => {
    if (sessionDisabled === null) {
        sessionDisabled = false;
        try {
            sessionDisabled = window.sessionStorage.getItem(SESSION_KEY) === '1';
        } catch (e) {
            // Storage blocked (private mode, sandbox): treat as not disabled.
        }
    }
    return sessionDisabled;
};

/* Turns routing off for the rest of this browser session (module flag + sessionStorage). */
export const disableImageCdnForSession = () => {
    sessionDisabled = true;
    try {
        window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) {
        // Storage blocked: the module flag still covers this page load.
    }
};

const shouldRoute = (url: unknown): url is string => {
    return (imageCdn as boolean) === true
        && typeof url === 'string'
        && url.startsWith(FIREBASE_STORAGE_PREFIX)
        && !isSessionDisabled();
};

const buildCdnUrl = (url: string, width: number) => {
    return `${CDN_PATH}?url=${encodeURIComponent(url)}&w=${width}`;
};

/* The CDN URL for a Firebase Storage image at `width`, or `url` unchanged when not routed. */
export function cdnImage<T>(url: T, width: number): T | string {
    if (!shouldRoute(url)) {
        return url;
    }
    return buildCdnUrl(url, width);
}

/* A `srcset` value ("<cdn url> 800w, …") for a Firebase Storage image, or undefined when not routed. */
export const cdnSrcSet = (url: unknown, widths: number[]): string | undefined => {
    if (!shouldRoute(url) || !Array.isArray(widths) || widths.length === 0) {
        return undefined;
    }
    return widths.map((width) => { return `${buildCdnUrl(url, width)} ${width}w`; }).join(', ');
};

/* The original image URL inside a `/.netlify/images?url=…` URL, or null. */
export const originalImageUrl = (cdnUrl: string | null | undefined): string | null => {
    if (typeof cdnUrl !== 'string' || !cdnUrl.includes(CDN_MARKER)) {
        return null;
    }
    try {
        const original = new URL(cdnUrl, window.location.href).searchParams.get('url');
        return typeof original === 'string' && original.startsWith(FIREBASE_STORAGE_PREFIX) ? original : null;
    } catch (e) {
        return null;
    }
};

/* The original URL behind the first candidate of a CDN `srcset` ("<cdn url> 800w, …"). */
const originalFromSrcSet = (srcSet: string | null): string | null => {
    const first = (srcSet || '').split(',')[0].trim().split(/\s+/)[0];
    return originalImageUrl(first);
};

/* Points a <picture>'s CDN <source>s back at their original URL (keeps the art direction,
   e.g. the mobile hero); a source whose URL can't be recovered loses its srcset. */
const restoreSiblingSources = (img: HTMLImageElement) => {
    const parent = img.parentElement;
    if (!parent || parent.tagName !== 'PICTURE') {
        return;
    }
    Array.prototype.forEach.call(parent.children, (child: Element) => {
        const srcSet = child.getAttribute('srcset');
        if (child.tagName !== 'SOURCE' || !srcSet || !srcSet.includes(CDN_MARKER)) {
            return;
        }
        const original = originalFromSrcSet(srcSet);
        child.removeAttribute('sizes');
        if (original) {
            child.setAttribute('srcset', original);
        } else {
            child.removeAttribute('srcset');
        }
    });
};

/* Images swapped back to their original, waiting for it to load: value = its load listener. */
const awaitingOriginal: WeakMap<HTMLImageElement, () => void> = new WeakMap();

const stopAwaitingOriginal = (img: HTMLImageElement) => {
    const onLoad = awaitingOriginal.get(img);
    if (onLoad) {
        img.removeEventListener('load', onLoad);
        awaitingOriginal.delete(img);
    }
};

/* Counts a CDN failure only once the swapped-in original actually loads, so an image that is
   broken at the source (deleted file) does not count against the CDN. (A later error on the
   same image cancels this in handleImageCdnError.) */
const countFailureWhenOriginalLoads = (img: HTMLImageElement) => {
    const onLoad = () => {
        awaitingOriginal.delete(img);
        if ((img.currentSrc || img.getAttribute('src') || '').includes(CDN_MARKER)) {
            return; // a newer CDN URL loaded, not the original
        }
        failureCount += 1;
        if (failureCount >= FAILURES_BEFORE_DISABLE) {
            disableImageCdnForSession();
        }
    };
    awaitingOriginal.set(img, onLoad);
    img.addEventListener('load', onLoad, { once: true });
};

/* Error handler: swaps a failed CDN image (and its <picture> sources) back to the original
   Firebase URL. Runs for every failed image; the session disable only stops new routing. */
export const handleImageCdnError = (event: Event) => {
    const target = event.target as Element | null;
    if (!target || target.tagName !== 'IMG') {
        return;
    }
    const img = target as HTMLImageElement;
    stopAwaitingOriginal(img); // an earlier swapped-in original failed too: not the CDN's fault
    const srcOriginal = originalImageUrl(img.getAttribute('src'));
    const currentOriginal = originalImageUrl(img.currentSrc) || originalFromSrcSet(img.getAttribute('srcset'));
    if (!srcOriginal && !currentOriginal) {
        return;
    }
    countFailureWhenOriginalLoads(img);
    restoreSiblingSources(img);
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    if (srcOriginal) {
        img.setAttribute('src', srcOriginal);
    } else if (currentOriginal && !img.getAttribute('src')) {
        img.setAttribute('src', currentOriginal);
    }
};

/* Registers the one capture-phase listener (image `error` events do not bubble). Returns a
   function that removes it again (used by tests). */
export const installImageCdnFallback = (): (() => void) => {
    const uninstall = () => {
        window.removeEventListener('error', handleImageCdnError, true);
        fallbackInstalled = false;
    };
    if (fallbackInstalled || typeof window === 'undefined') {
        return uninstall;
    }
    fallbackInstalled = true;
    window.addEventListener('error', handleImageCdnError, true);
    return uninstall;
};
